/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';
import IncidentDrawerOpsTab from './IncidentDrawerOpsTab';
import IncidentDrawerSupportPanel from './IncidentDrawerSupportPanel';
import IncidentDetailHeader from './IncidentDetailHeader';
import IncidentSummaryCard from './IncidentSummaryCard';
import IncidentWorkflowRail from './IncidentWorkflowRail';
import RoutingIntegrationStatus from './RoutingIntegrationStatus';
import { GroupedWorkflowContainer } from './GroupedIncidentReview';
import { GroupedIncidentGuidedWorkflow } from './GroupedIncidentGuidedWorkflow';
import { GroupedGuidedAdvancedDetails } from './GroupedGuidedAdvancedDetails';
import { GroupedVerifyEvidencePanel } from './GroupedVerifyEvidencePanel';
import { IncidentDrawerDetailStep2Status } from './IncidentDrawerDetailStep2Status';
import { formatWorkflowStateLabel } from '../../utils/incidentStateLabels';
import type { IncidentDrawerDetailScope } from './incidentDrawerDetailTypes';
import { IncidentDrawerDetailStep0 } from './IncidentDrawerDetailStep0';
import { IncidentDrawerDetailStep1 } from './IncidentDrawerDetailStep1';
import { IncidentDrawerDetailStep2Evidence } from './IncidentDrawerDetailStep2Evidence';
import { IncidentDrawerDetailStep3 } from './IncidentDrawerDetailStep3';
import { IncidentDrawerDetailStep4 } from './IncidentDrawerDetailStep4';
import { IncidentDrawerDetailStep5 } from './IncidentDrawerDetailStep5';
import { IncidentDrawerDetailStep8Closeout } from './IncidentDrawerDetailStep8Closeout';
import { IncidentDrawerDetailAdvanced } from './IncidentDrawerDetailAdvanced';
import IncidentSafetyEpisodeSummaryPanel from './IncidentSafetyEpisodeSummaryPanel';
import IncidentSafetyEpisodeTimeline from './IncidentSafetyEpisodeTimeline';
import InvestigateControlLinkCard from './InvestigateControlLinkCard';
import type { IncidentSafetyEpisode } from '../../services/incidentsApi';
import {
  PATTERN_IMPACT_LEGACY_HELPER,
  PATTERN_IMPACT_NOT_ANCHOR_HELPER,
  PATTERN_VERIFY_OBSERVATIONS_ACTION,
  patternHasAuthoritativePendingVerification,
  patternOwnershipHasLinkedCase,
} from '../../domain/incidents/patternOwnershipContainment';
import { buildSafetyCaseVerifyPath } from '../../domain/safetyCases/investigateControlLink';

export type { IncidentDrawerDetailScope } from './incidentDrawerDetailTypes';

const renderManagedInSafetyCasePanel = (s: any) => {
  const caseId = String(s.safetyCaseId || '').trim();
  const pendingVerify = patternHasAuthoritativePendingVerification(
    s.pendingObservationVerificationCount,
  );
  const verifyHref = caseId
    ? buildSafetyCaseVerifyPath({
      safetyCaseId: caseId,
      orgId: String(s.activeScope?.orgId || s.selectedIncident?.org_id || '').trim() || null,
      siteId: String(s.scopedSiteId || s.activeScope?.siteId || s.selectedIncident?.site_id || '').trim() || null,
      workflowId: s.workflowId,
      incidentId: String(s.selectedIncident?.incident_id || '').trim() || null,
    })
    : '';
  return (
    <Box data-testid="pattern-managed-in-safety-case">
      <InvestigateControlLinkCard
        safetyCaseId={s.safetyCaseId}
        orgId={String(s.activeScope?.orgId || s.selectedIncident?.org_id || '').trim() || null}
        siteId={String(s.scopedSiteId || s.activeScope?.siteId || s.selectedIncident?.site_id || '').trim() || null}
        workflowId={s.workflowId}
        incidentId={String(s.selectedIncident?.incident_id || '').trim() || null}
        incident={s.selectedIncident}
        workflowCorrectiveActions={s.workflowCorrectiveActions}
        navigate={s.navigate}
      />
      {pendingVerify && verifyHref ? (
        <Box sx={{ mt: 1.25 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => s.navigate?.(verifyHref)}
            sx={{ fontWeight: 800 }}
            data-testid="pattern-verify-observations"
          >
            {PATTERN_VERIFY_OBSERVATIONS_ACTION}
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, lineHeight: 1.5 }}>
            Formal Scan verification happens on Safety Scan Verify, not in Investigate.
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
};

const renderLegacyPatternImpactWaiver = (s: any) => (
  <Box data-testid="pattern-legacy-impact-waiver" sx={{ mt: 1.5 }}>
    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
      Legacy operator context / waiver
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1, lineHeight: 1.55 }}>
      {PATTERN_IMPACT_LEGACY_HELPER}
      {' '}
      {PATTERN_IMPACT_NOT_ANCHOR_HELPER}
    </Typography>
    <IncidentDrawerOpsTab
      groupedReviewMode={s.groupedReviewMode}
      visibleSections="track_impact"
      trackImpactReady={s.groupedTrackImpactReady}
      selectedIncident={s.selectedIncident}
      opsStateDraft={s.opsStateDraft}
      setOpsStateDraft={s.setOpsStateDraft}
      opsAssigneeDraft={s.opsAssigneeDraft}
      setOpsAssigneeDraft={s.setOpsAssigneeDraft}
      opsTeamDraft={s.opsTeamDraft}
      setOpsTeamDraft={s.setOpsTeamDraft}
      opsSlaTargetDraft={s.opsSlaTargetDraft}
      setOpsSlaTargetDraft={s.setOpsSlaTargetDraft}
      measurementBaselineDraft={s.measurementBaselineDraft}
      setMeasurementBaselineDraft={s.setMeasurementBaselineDraft}
      measurementFollowUpDraft={s.measurementFollowUpDraft}
      setMeasurementFollowUpDraft={s.setMeasurementFollowUpDraft}
      measurementMeasuredAtDraft={s.measurementMeasuredAtDraft}
      setMeasurementMeasuredAtDraft={s.setMeasurementMeasuredAtDraft}
      measurementOutcomeStatusDraft={s.measurementOutcomeStatusDraft}
      setMeasurementOutcomeStatusDraft={s.setMeasurementOutcomeStatusDraft}
      measurementNoteDraft={s.measurementNoteDraft}
      setMeasurementNoteDraft={s.setMeasurementNoteDraft}
      measurementImpactWaivedDraft={s.measurementImpactWaivedDraft}
      setMeasurementImpactWaivedDraft={s.setMeasurementImpactWaivedDraft}
      canUseOpsAutomation={s.canUseOpsAutomation}
      drawerBusy={s.drawerBusy}
      handleSaveOps={s.handleSaveOps}
      handleSaveMeasurement={s.handleSaveMeasurement}
      workflowId={s.workflowId}
      workflowCorrectiveActions={s.workflowCorrectiveActions}
      workflowImpactSummary={s.workflowImpactSummary}
      workflowClaimReadiness={s.workflowClaimReadiness}
      refreshWorkflowCorrectiveSummary={s.refreshWorkflowCorrectiveSummary}
      handleSaveCoverageReviewAttestation={s.handleSaveCoverageReviewAttestation}
      handleSaveZoneControlsConfirmation={s.handleSaveZoneControlsConfirmation}
      handleAddWorkflowEvidenceNote={s.handleAddWorkflowEvidenceNote}
      handleUpdateWorkflowActionStatus={s.handleUpdateWorkflowActionStatus}
      workflowFeedback={s.workflowFeedback}
      runbookTemplates={s.runbookTemplates}
      incidentRunbookBusy={s.incidentRunbookBusy}
      setIncidentRunbookBusy={s.setIncidentRunbookBusy}
      executeRunbookServer={s.executeRunbookServer}
      navigate={s.navigate}
      user={s.user}
      canRunIncidentRunbooks={s.canRunIncidentRunbooks}
      renderMode={s.renderMode}
      step6Ref={s.drawerStep6Ref}
      step7Ref={s.drawerStep7Ref}
      step6Navigation={null}
      step7Navigation={null}
    />
  </Box>
);

const renderObservationQualifyingEntriesLedger = (scope: IncidentDrawerDetailScope) => {
  const s = scope as any;
  if (!s.selectedIncident) return null;
  const loadedEpisodes: IncidentSafetyEpisode[] = s.incidentSafetyEpisodes || [];
  const hasLoadedLedger = Boolean(s.incidentSafetyEpisodesAvailable) && loadedEpisodes.length > 0;
  const summaryIncident = hasLoadedLedger
    && (s.selectedIncident.episode_count === null
      || s.selectedIncident.episode_count === undefined)
    ? {
        ...s.selectedIncident,
        episode_count: loadedEpisodes.length,
        active_episode_count:
          loadedEpisodes.filter((ep: IncidentSafetyEpisode) => (
            String(ep.status || '').toLowerCase() === 'active'
          )).length,
        observed_track_count: new Set(
          loadedEpisodes
            .map((ep: IncidentSafetyEpisode) => String(ep.local_track_id || ep.episode_id || ''))
            .filter(Boolean),
        ).size,
        evidence_count:
          s.selectedIncident.evidence_count
          ?? loadedEpisodes.filter((ep: IncidentSafetyEpisode) => (
            Boolean(ep.evidence_id || ep.snapshot_id || ep.clip_id)
          )).length,
        safety_episodes_available: true,
      }
    : s.selectedIncident;
  const episodeFamily = loadedEpisodes.find((episode) => episode.family)?.family;
  return (
    <Box data-testid="observation-qualifying-entries-ledger" sx={{ mb: 1.5 }}>
      <IncidentSafetyEpisodeSummaryPanel
        incident={summaryIncident}
        episodeFamily={episodeFamily}
      />
      <IncidentSafetyEpisodeTimeline
        episodes={loadedEpisodes}
        loading={Boolean(s.incidentSafetyEpisodesLoading)}
        error={s.incidentSafetyEpisodesError || null}
        available={
          typeof s.incidentSafetyEpisodesAvailable === 'boolean'
            ? s.incidentSafetyEpisodesAvailable
            : null
        }
        incidentType={s.selectedIncident?.incident_type}
        onViewMoment={s.handleViewEvidenceMoment}
      />
    </Box>
  );
};

const renderGroupedAuditSteps = (scope: IncidentDrawerDetailScope) => {
  const observationDispositionMode = Boolean((scope as any).observationDispositionMode);
  if (observationDispositionMode) {
    return (
      <>
        {renderObservationQualifyingEntriesLedger(scope)}
        <IncidentDrawerDetailStep2Evidence scope={scope} />
        <IncidentDrawerDetailStep5 scope={scope} />
      </>
    );
  }
  return (
  <>
    <IncidentDrawerDetailStep0 scope={scope} />
    <Box>
      <IncidentDrawerDetailStep1 scope={scope} />
      <IncidentDrawerDetailStep2Evidence scope={scope} />
      <IncidentDrawerDetailStep3 scope={scope} />
      <IncidentDrawerDetailStep4 scope={scope} />
      <IncidentDrawerDetailStep5 scope={scope} />
    </Box>
    <IncidentDrawerOpsTab
      groupedReviewMode={(scope as any).groupedReviewMode}
      selectedIncident={(scope as any).selectedIncident}
      opsStateDraft={(scope as any).opsStateDraft}
      setOpsStateDraft={(scope as any).setOpsStateDraft}
      opsAssigneeDraft={(scope as any).opsAssigneeDraft}
      setOpsAssigneeDraft={(scope as any).setOpsAssigneeDraft}
      opsTeamDraft={(scope as any).opsTeamDraft}
      setOpsTeamDraft={(scope as any).setOpsTeamDraft}
      opsSlaTargetDraft={(scope as any).opsSlaTargetDraft}
      setOpsSlaTargetDraft={(scope as any).setOpsSlaTargetDraft}
      measurementBaselineDraft={(scope as any).measurementBaselineDraft}
      setMeasurementBaselineDraft={(scope as any).setMeasurementBaselineDraft}
      measurementFollowUpDraft={(scope as any).measurementFollowUpDraft}
      setMeasurementFollowUpDraft={(scope as any).setMeasurementFollowUpDraft}
      measurementMeasuredAtDraft={(scope as any).measurementMeasuredAtDraft}
      setMeasurementMeasuredAtDraft={(scope as any).setMeasurementMeasuredAtDraft}
      measurementOutcomeStatusDraft={(scope as any).measurementOutcomeStatusDraft}
      setMeasurementOutcomeStatusDraft={(scope as any).setMeasurementOutcomeStatusDraft}
      measurementNoteDraft={(scope as any).measurementNoteDraft}
      setMeasurementNoteDraft={(scope as any).setMeasurementNoteDraft}
      measurementImpactWaivedDraft={(scope as any).measurementImpactWaivedDraft}
      setMeasurementImpactWaivedDraft={(scope as any).setMeasurementImpactWaivedDraft}
      correctiveActionTypeDraft={(scope as any).correctiveActionTypeDraft}
      setCorrectiveActionTypeDraft={(scope as any).setCorrectiveActionTypeDraft}
      correctiveActionPriorityDraft={(scope as any).correctiveActionPriorityDraft}
      setCorrectiveActionPriorityDraft={(scope as any).setCorrectiveActionPriorityDraft}
      correctiveActionDueDateDraft={(scope as any).correctiveActionDueDateDraft}
      setCorrectiveActionDueDateDraft={(scope as any).setCorrectiveActionDueDateDraft}
      correctiveActionVerificationMethodDraft={(scope as any).correctiveActionVerificationMethodDraft}
      setCorrectiveActionVerificationMethodDraft={(scope as any).setCorrectiveActionVerificationMethodDraft}
      correctiveActionControlReviewDraft={(scope as any).correctiveActionControlReviewDraft}
      setCorrectiveActionControlReviewDraft={(scope as any).setCorrectiveActionControlReviewDraft}
      canUseOpsAutomation={(scope as any).canUseOpsAutomation}
      drawerBusy={(scope as any).drawerBusy}
      handleSaveOps={(scope as any).handleSaveOps}
      handleSaveMeasurement={(scope as any).handleSaveMeasurement}
      handleSaveCorrectiveAction={(scope as any).handleSaveCorrectiveAction}
      workflowId={(scope as any).workflowId}
      workflowCorrectiveActions={(scope as any).workflowCorrectiveActions}
      workflowImpactSummary={(scope as any).workflowImpactSummary}
      workflowClaimReadiness={(scope as any).workflowClaimReadiness}
      refreshWorkflowCorrectiveSummary={(scope as any).refreshWorkflowCorrectiveSummary}
      handleSaveCoverageReviewAttestation={(scope as any).handleSaveCoverageReviewAttestation}
      handleSaveZoneControlsConfirmation={(scope as any).handleSaveZoneControlsConfirmation}
      handleAddWorkflowEvidenceNote={(scope as any).handleAddWorkflowEvidenceNote}
      handleUpdateWorkflowActionStatus={(scope as any).handleUpdateWorkflowActionStatus}
      workflowFeedback={(scope as any).workflowFeedback}
      runbookTemplates={(scope as any).runbookTemplates}
      incidentRunbookBusy={(scope as any).incidentRunbookBusy}
      setIncidentRunbookBusy={(scope as any).setIncidentRunbookBusy}
      executeRunbookServer={(scope as any).executeRunbookServer}
      navigate={(scope as any).navigate}
      user={(scope as any).user}
      canRunIncidentRunbooks={(scope as any).canRunIncidentRunbooks}
      renderMode={(scope as any).renderMode}
      step6Ref={(scope as any).drawerStep6Ref}
      step7Ref={(scope as any).drawerStep7Ref}
      step6Navigation={(scope as any).renderStepNavigation('step6')}
      step7Navigation={(scope as any).renderStepNavigation('step7')}
    />
    <IncidentDrawerDetailStep8Closeout scope={scope} />
  </>
  );
};

const renderGroupedAdvancedDetails = (s: any) => (
  <GroupedGuidedAdvancedDetails
    expanded={s.drawerAdvancedOpen}
    onExpandedChange={s.setDrawerAdvancedOpen}
  >
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, lineHeight: 1.55 }}>
      Edge queue status, cloud lag, proof timing, delivery health, policy and device identifiers, notification channels, audit history, and support tools.
    </Typography>
    {renderLegacyPatternImpactWaiver(s)}
    <IncidentDrawerDetailStep2Status scope={s} view="technical" />
    <RoutingIntegrationStatus
      groupedReviewMode={s.groupedReviewMode}
      selectedIncident={s.selectedIncident}
      getOdooStateColor={s.getOdooStateColor}
    />
    <IncidentDrawerSupportPanel
      drawerCardSx={s.drawerCardSx}
      cardHeadingSx={s.cardHeadingSx}
      selectedIncident={s.selectedIncident}
      derivePolicyNotificationRoutes={s.derivePolicyNotificationRoutes}
      PolicyNotificationStatusChip={s.PolicyNotificationStatusChip}
      notificationEditorError={s.notificationEditorError}
      policyScopeForEditing={s.policyScopeForEditing}
      canUseOpsAutomation={s.canUseOpsAutomation}
      setNotificationEditorOpen={s.setNotificationEditorOpen}
      handleOpenPolicyEditor={s.handleOpenPolicyEditor}
      detectionRuleSummary={s.detectionRuleSummary}
      handleSyncFromOdoo={s.handleSyncFromOdoo}
      canOpenAuditTrail={s.canOpenAuditTrail}
      openAuditTrail={s.openAuditTrail}
      canOpenCopilot={s.canOpenCopilot}
      navigate={s.navigate}
      buildContextBundle={s.buildContextBundle}
      activeScope={s.activeScope}
      scopedSiteId={s.scopedSiteId}
      opsStateResult={s.opsStateResult}
      user={s.user}
      notificationEditorOpen={s.notificationEditorOpen}
      emailTargetDraft={s.emailTargetDraft}
      setEmailTargetDraft={s.setEmailTargetDraft}
      smsTargetDraft={s.smsTargetDraft}
      setSmsTargetDraft={s.setSmsTargetDraft}
      webhookTargetDraft={s.webhookTargetDraft}
      setWebhookTargetDraft={s.setWebhookTargetDraft}
      notificationEditorDeployNow={s.notificationEditorDeployNow}
      setNotificationEditorDeployNow={s.setNotificationEditorDeployNow}
      notificationEditorBusy={s.notificationEditorBusy}
      handleSaveNotificationTargets={s.handleSaveNotificationTargets}
    />
    <IncidentDrawerDetailAdvanced scope={s} bare />
  </GroupedGuidedAdvancedDetails>
);

const renderObservationAdvancedDetails = (s: any, scope: IncidentDrawerDetailScope) => (
  <GroupedGuidedAdvancedDetails
    expanded={s.drawerAdvancedOpen}
    onExpandedChange={s.setDrawerAdvancedOpen}
    summaryLabel="Ops / advanced details"
  >
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, lineHeight: 1.55 }}>
      Policy context, delivery health, work-order state, notification channels, and support tools are available here when you need operational detail beyond this single-observation disposition.
    </Typography>
    <IncidentSummaryCard
      selectedIncident={s.selectedIncident}
      workflowLabel={formatWorkflowStateLabel(s.selectedIncident)}
      opsStateLabel={s.opsStateLabel}
      explanation={s.summaryExplanation}
      currentUser={String(s.user?.username || '')}
      policyContext={s.policyContext}
    />
    <RoutingIntegrationStatus
      groupedReviewMode={s.groupedReviewMode}
      selectedIncident={s.selectedIncident}
      getOdooStateColor={s.getOdooStateColor}
    />
    <IncidentDrawerSupportPanel
      drawerCardSx={s.drawerCardSx}
      cardHeadingSx={s.cardHeadingSx}
      selectedIncident={s.selectedIncident}
      derivePolicyNotificationRoutes={s.derivePolicyNotificationRoutes}
      PolicyNotificationStatusChip={s.PolicyNotificationStatusChip}
      notificationEditorError={s.notificationEditorError}
      policyScopeForEditing={s.policyScopeForEditing}
      canUseOpsAutomation={s.canUseOpsAutomation}
      setNotificationEditorOpen={s.setNotificationEditorOpen}
      handleOpenPolicyEditor={s.handleOpenPolicyEditor}
      detectionRuleSummary={s.detectionRuleSummary}
      handleSyncFromOdoo={s.handleSyncFromOdoo}
      canOpenAuditTrail={s.canOpenAuditTrail}
      openAuditTrail={s.openAuditTrail}
      canOpenCopilot={s.canOpenCopilot}
      navigate={s.navigate}
      buildContextBundle={s.buildContextBundle}
      activeScope={s.activeScope}
      scopedSiteId={s.scopedSiteId}
      opsStateResult={s.opsStateResult}
      user={s.user}
      notificationEditorOpen={s.notificationEditorOpen}
      emailTargetDraft={s.emailTargetDraft}
      setEmailTargetDraft={s.setEmailTargetDraft}
      smsTargetDraft={s.smsTargetDraft}
      setSmsTargetDraft={s.setSmsTargetDraft}
      webhookTargetDraft={s.webhookTargetDraft}
      setWebhookTargetDraft={s.setWebhookTargetDraft}
      notificationEditorDeployNow={s.notificationEditorDeployNow}
      setNotificationEditorDeployNow={s.setNotificationEditorDeployNow}
      notificationEditorBusy={s.notificationEditorBusy}
      handleSaveNotificationTargets={s.handleSaveNotificationTargets}
    />
    <IncidentDrawerDetailAdvanced scope={scope} bare />
  </GroupedGuidedAdvancedDetails>
);

export const IncidentDrawerDetailContent: React.FC<IncidentDrawerDetailContentProps> = ({ scope }) => {
  const s = scope as any;

  return (
    <>
      {!s.showGroupedPageTitleChrome ? (
        <IncidentDetailHeader
          displayTitle={s.currentTitle}
          summarySentence={s.oneLineSummary}
          selectedIncident={s.selectedIncident}
          packLabel={s.packPolicyHeader.packLabel || null}
          zoneName={
            s.selectedIncident
              ? s.selectedZoneName
              : (s.selectedGroupContext as any)
                ? s.selectedGroupZoneName
                : null
          }
          cameraName={
            s.selectedIncident
              ? s.selectedCameraName
              : (s.selectedGroupContext as any)
                ? s.selectedGroupCameraName
                : null
          }
          lifecycleLabel={
            s.selectedIncident
              ? String(formatWorkflowStateLabel(s.selectedIncident) || s.workflowLabel)
              : s.workflowLabel
          }
          onClose={() => s.setViewDialogOpen(false)}
          actionMode={s.isPageMode ? 'back' : 'close'}
          contextChips={s.contextChips}
          showIncidentIdInMeta={!!s.selectedIncident?.incident_id}
        />
      ) : null}

      {s.selectedGroupContext && !s.selectedIncident?.incident_id && (
        <Paper variant="outlined" sx={s.drawerCardSx}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Loading the most recent related incident…
          </Typography>
        </Paper>
      )}

      {s.selectedIncident && (
        <Box
          ref={s.drawerScrollRef}
          data-testid={s.isPageMode ? 'incident-review-scroll-region' : 'incident-drawer-scroll-region'}
          sx={
            s.isPageMode
              ? { overflow: 'visible', maxHeight: 'none', pr: 0 }
              : { overflow: 'auto', maxHeight: 'calc(100vh - 236px)', pr: 0.5 }
          }
        >
          {s.workflowReviewContextLead}
          {s.groupedReviewLead}
          {/* Standalone incident detail (non-grouped). Observation disposition
              renders the qualifying-entries ledger inside renderGroupedAuditSteps instead. */}
          {s.selectedIncident && !s.groupedReviewMode && !s.observationDispositionMode ? (
            renderObservationQualifyingEntriesLedger(scope)
          ) : null}
          {s.selectedIncident && !s.groupedReviewMode && !s.observationDispositionMode ? (
            <IncidentSummaryCard
              selectedIncident={s.selectedIncident}
              workflowLabel={formatWorkflowStateLabel(s.selectedIncident)}
              opsStateLabel={s.opsStateLabel}
              explanation={s.summaryExplanation}
              currentUser={String(s.user?.username || '')}
              policyContext={s.policyContext}
            />
          ) : null}

          {s.groupedReviewMode ? (
            <GroupedIncidentGuidedWorkflow
              progress={s.groupedWorkflowProgress}
              relatedCount={s.relatedIncidentCount}
              representativeIncidentId={s.representativeIncidentId}
              reportBlocked={s.groupedReportBlocked}
              reportBlockedReason={s.groupedReportBlockedReason}
              signOffMissingItems={s.groupedReportSignOffMissingItems}
              onPrimaryAction={s.handleGroupedGuidedPrimaryAction}
              onSecondaryAction={s.handleGroupedGuidedSecondaryAction}
              onRegisterPanelControl={s.registerGroupedGuidedPanelControl}
              initialPanelId={s.groupedGuidedInitialPanelId}
              localProgressHint={s.groupedLocalProgressHint}
              onClearLocalProgress={s.handleClearGroupedLocalProgress}
              onExpandedPanelChange={s.handleGroupedGuidedExpandedPanelChange}
              hasLinkedSafetyCase={Boolean(s.hasLinkedSafetyCase) || patternOwnershipHasLinkedCase(s.safetyCaseId)}
              panelStatusContext={{
                representativeReviewComplete: s.groupedRepresentativeReviewComplete,
                evidenceConfirmed: s.groupedEvidenceConfirmed,
                correctiveActionLocked: s.groupedCorrectiveActionIsLocked,
                correctiveWorkflowPhase: s.groupedCorrectiveWorkflowPhase,
                reportDraftAvailable: typeof s._openReportDialog === 'function',
                reportSignOffBlocked: s.groupedReportBlocked,
                followUpOwnerLabel: s.followUpOwnerSummary,
                trackImpactReady: s.groupedTrackImpactReady,
                followUpMeasurementRecorded: s.groupedFollowUpMeasurementRecorded,
              }}
              panels={{
                understand_risk: s.groupedUnderstandRiskPanel ?? null,
                verify_evidence: <GroupedVerifyEvidencePanel scope={scope} />,
                decide_action: (
                  Boolean(s.hasLinkedSafetyCase) || patternOwnershipHasLinkedCase(s.safetyCaseId)
                )
                  ? renderManagedInSafetyCasePanel(s)
                  : null,
              }}
              advancedDetails={renderGroupedAdvancedDetails(s)}
            />
          ) : (
            <GroupedWorkflowContainer
              groupedReviewMode={s.groupedReviewMode}
              groupedDeepReviewOpen={s.groupedDeepReviewOpen}
              setGroupedDeepReviewOpen={s.setGroupedDeepReviewOpen}
              groupedProgressSummary={s.groupedProgressSummary}
              relatedCount={s.relatedIncidentCount}
              representativeIncidentId={s.representativeIncidentId}
              auditPreamble={null}
            >
              {!s.observationDispositionMode ? (
                <IncidentWorkflowRail
                  railRef={s.journeyRailRef}
                  isPageMode={s.isPageMode}
                  observationDispositionMode={Boolean((scope as any).observationDispositionMode)}
                  incidentId={s.selectedIncident?.incident_id}
                  onOpenReviewPage={s.onOpenReviewPage}
                  journeyPhases={s.journeyPhases}
                  activeJourneyPhase={s.activeJourneyPhase}
                  journeyPhaseMeta={s.journeyPhaseMeta}
                  scrollToJourneyPhase={s.scrollToJourneyPhase}
                  nextIncompleteJourneyStep={s.nextIncompleteJourneyStep}
                  activeJourneyStep={s.activeJourneyStep}
                  journeySteps={s.journeySteps}
                />
              ) : null}
              {renderGroupedAuditSteps(scope)}
              {s.observationDispositionMode ? (
                renderObservationAdvancedDetails(s, scope)
              ) : (
                <>
                  <RoutingIntegrationStatus
                    groupedReviewMode={s.groupedReviewMode}
                    selectedIncident={s.selectedIncident}
                    getOdooStateColor={s.getOdooStateColor}
                  />
                  <IncidentDrawerSupportPanel
                    drawerCardSx={s.drawerCardSx}
                    cardHeadingSx={s.cardHeadingSx}
                    selectedIncident={s.selectedIncident}
                    derivePolicyNotificationRoutes={s.derivePolicyNotificationRoutes}
                    PolicyNotificationStatusChip={s.PolicyNotificationStatusChip}
                    notificationEditorError={s.notificationEditorError}
                    policyScopeForEditing={s.policyScopeForEditing}
                    canUseOpsAutomation={s.canUseOpsAutomation}
                    setNotificationEditorOpen={s.setNotificationEditorOpen}
                    handleOpenPolicyEditor={s.handleOpenPolicyEditor}
                    detectionRuleSummary={s.detectionRuleSummary}
                    handleSyncFromOdoo={s.handleSyncFromOdoo}
                    canOpenAuditTrail={s.canOpenAuditTrail}
                    openAuditTrail={s.openAuditTrail}
                    canOpenCopilot={s.canOpenCopilot}
                    navigate={s.navigate}
                    buildContextBundle={s.buildContextBundle}
                    activeScope={s.activeScope}
                    scopedSiteId={s.scopedSiteId}
                    opsStateResult={s.opsStateResult}
                    user={s.user}
                    notificationEditorOpen={s.notificationEditorOpen}
                    emailTargetDraft={s.emailTargetDraft}
                    setEmailTargetDraft={s.setEmailTargetDraft}
                    smsTargetDraft={s.smsTargetDraft}
                    setSmsTargetDraft={s.setSmsTargetDraft}
                    webhookTargetDraft={s.webhookTargetDraft}
                    setWebhookTargetDraft={s.setWebhookTargetDraft}
                    notificationEditorDeployNow={s.notificationEditorDeployNow}
                    setNotificationEditorDeployNow={s.setNotificationEditorDeployNow}
                    notificationEditorBusy={s.notificationEditorBusy}
                    handleSaveNotificationTargets={s.handleSaveNotificationTargets}
                  />
                  <IncidentDrawerDetailAdvanced scope={scope} />
                </>
              )}
            </GroupedWorkflowContainer>
          )}
        </Box>
      )}
    </>
  );
};

export type IncidentDrawerDetailContentProps = {
  scope: IncidentDrawerDetailScope;
};

export default IncidentDrawerDetailContent;
